import {Button, CircularProgress, Grid, ListItem, ListItemIcon, ListItemText, MenuItem} from '@material-ui/core';
import Menu from '@material-ui/core/Menu';
import ArrowDown from '@material-ui/icons/KeyboardArrowDown';
import 'Core/components/Navigation/MyAccessMenu.sass';
import {ModalState, showModal} from 'Core/ducks/modal';
import {AsyncResult, Empty, isLoaded} from 'Core/models/Async';
import {isRight} from 'fp-ts/lib/Either';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import MenuStateProvider from 'Shared/components/MenuStateProvider';
import NestedList from 'Shared/components/NestedList';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {useWidth} from 'Shared/utilities/UseWidth';
import {SsoResponseDto} from 'Users/api';
import {changeLegacyForms, getLegacyForms} from 'Users/ducks/SSO/legacyForm';

type LegacyAccessMenuProps = WithSelectedAgencyProps &
  WithClaimsProps & {
    onSidebarClose?: () => void;
  };

const LegacyAccessMenu = ({claims, selectedAgency, onSidebarClose}: LegacyAccessMenuProps) => {
  const dispatch = useDispatch();
  const width = useWidth();
  const isMobileView = ['xs', 'sm', 'md'].includes(width);

  const {legacyForm, interactiveLegacyForm, legacyBulletin} = useSelector(
    (state: ReducerState) => state.user.sso.legacyForm
  );

 const isSafeUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const redirect = (form: AsyncResult<SsoResponseDto>) => {
    if (isRight(form) && form.right.url && isSafeUrl(form.right.url)) {
      window.open(form.right.url);
    } else {
      dispatch(showModal(ModalState.LegacyAccess()));
    }

    if (onSidebarClose) {
      onSidebarClose();
    }

    clearData();
  };

  const clearData = () => {
    dispatch(changeLegacyForms(new Empty(), new Empty(), new Empty()));
  };
  const reloadData = () => {
    dispatch(getLegacyForms(selectedAgency.encryptedIdentifier, claims.id, selectedAgency.isNewYork));
  };

  const MobileView = (
    <NestedList mainListItemTitle="Forms" isExpanded={false} onExpand={reloadData} onCollapse={clearData}>
      {isLoaded(legacyForm) && isLoaded(interactiveLegacyForm) && isLoaded(legacyBulletin) ? (
        <>
          <ListItem button onClick={() => redirect(legacyForm.Data)}>
            <ListItemIcon>
              <i className="fas fa-external-link-alt sidebar__menu-icon" />
            </ListItemIcon>
            <ListItemText>Marketing Forms</ListItemText>
          </ListItem>
          <ListItem button onClick={() => redirect(interactiveLegacyForm.Data)}>
            <ListItemIcon>
              <i className="fas fa-external-link-alt sidebar__menu-icon" />
            </ListItemIcon>
            <ListItemText>Interactive Forms</ListItemText>
          </ListItem>
          <ListItem button onClick={() => redirect(legacyBulletin.Data)}>
            <ListItemIcon>
              <i className="fas fa-external-link-alt sidebar__menu-icon" />
            </ListItemIcon>
            <ListItemText>Bulletins</ListItemText>
          </ListItem>
        </>
      ) : (
        <Grid container justify="center">
          <CircularProgress color="primary" />
        </Grid>
      )}
    </NestedList>
  );

  return isMobileView ? (
    MobileView
  ) : (
    <MenuStateProvider>
      {({anchor, close, open}) => (
        <>
          <Button
            id="my-allstate-menu-button"
            className="my-access-menu"
            variant="text"
            onClick={event => {
              open(event.currentTarget);
              reloadData();
            }}
          >
            Forms
            <ArrowDown />
          </Button>
          <Menu
            disablePortal
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            transformOrigin={{vertical: 'top', horizontal: 'center'}}
            getContentAnchorEl={null}
            onClose={() => {
              close();
              clearData();
            }}
            open={Boolean(anchor)}
            anchorEl={anchor}
          >
            {isLoaded(legacyForm) && isLoaded(interactiveLegacyForm) && isLoaded(legacyBulletin) ? (
              <>
                <MenuItem
                  id="accounts-my-allstate-option"
                  className="my-access-menu__item"
                  onClick={() => {
                    redirect(legacyBulletin.Data);
                    close();
                  }}
                >
                  Bulletins
                </MenuItem>
                <MenuItem
                  id="accounts-my-allstate-option"
                  className="my-access-menu__item"
                  onClick={() => {
                    redirect(interactiveLegacyForm.Data);
                    close();
                  }}
                >
                  Interactive Forms
                </MenuItem>
                <MenuItem
                  id="accounts-my-allstate-option"
                  className="my-access-menu__item"
                  onClick={() => {
                    redirect(legacyForm.Data);
                    close();
                  }}
                >
                  Marketing Forms
                </MenuItem>
              </>
            ) : (
              <Grid container justify="center">
                <CircularProgress color="primary" size="2rem" className="my-access-menu__spinner" />
              </Grid>
            )}
          </Menu>
        </>
      )}
    </MenuStateProvider>
  );
};

export default withSelectedAgency(withUserClaims(LegacyAccessMenu));
